import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import NotificationDomain from "entities/domain/notifications/domain";
import { useMerchant } from "hooks/queries/use-settings-merchant";
import { useAppSelector } from "redux/hooks";
import NotificationsService from "services/notifications";
import { removeById } from "./query-cache-helpers";

// Query keys
export const notificationsQueryKey = (
  merchantId: number | null | undefined
) => ["notifications", merchantId ?? null];

/**
 * Hook to fetch notifications for a merchant
 */
export function useNotifications(staleTimeMs: number = 60_000) {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();

  return useQuery<NotificationDomain[], Error>({
    queryKey: notificationsQueryKey(merchant?.id),
    enabled: Boolean(merchant?.id),
    queryFn: async () => {
      if (!merchant?.id) return [];
      return NotificationsService.getNotifications(auth0Context, merchant.id);
    },
    staleTime: staleTimeMs,
  });
}

/**
 * Hook to mark a notification as read
 * Optimistically updates the cache
 */
export function useMarkNotificationRead() {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    string,
    { prevNotifications?: NotificationDomain[] }
  >({
    mutationFn: async (notificationId) => {
      if (!merchant?.id) {
        throw new Error("Merchant ID is required");
      }
      await NotificationsService.markNotificationAsRead(
        auth0Context,
        merchant.id,
        notificationId
      );
    },
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({
        queryKey: notificationsQueryKey(merchant?.id),
      });

      const prevNotifications = queryClient.getQueryData<NotificationDomain[]>(
        notificationsQueryKey(merchant?.id)
      );

      // Optimistically mark as read
      if (prevNotifications) {
        queryClient.setQueryData<NotificationDomain[]>(
          notificationsQueryKey(merchant?.id),
          prevNotifications.map((n) =>
            n.id === notificationId ? { ...n, read: true } : n
          )
        );
      }

      return { prevNotifications };
    },
    onError: (_err, _vars, context) => {
      if (context?.prevNotifications && merchant?.id) {
        queryClient.setQueryData(
          notificationsQueryKey(merchant.id),
          context.prevNotifications
        );
      }
    },
    onSuccess: () => {
      // Server response confirms the update, cache is already optimistically updated
    },
  });
}

/**
 * Hook to mark all notifications as read
 * Optimistically updates the cache
 */
export function useMarkAllNotificationsRead() {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    void,
    { prevNotifications?: NotificationDomain[] }
  >({
    mutationFn: async () => {
      if (!merchant?.id) {
        throw new Error("Merchant ID is required");
      }
      await NotificationsService.markAllNotificationsAsRead(
        auth0Context,
        merchant.id
      );
    },
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: notificationsQueryKey(merchant?.id),
      });

      const prevNotifications = queryClient.getQueryData<NotificationDomain[]>(
        notificationsQueryKey(merchant?.id)
      );

      // Optimistically mark all as read
      if (prevNotifications) {
        queryClient.setQueryData<NotificationDomain[]>(
          notificationsQueryKey(merchant?.id),
          prevNotifications.map((n) => ({ ...n, read: true }))
        );
      }

      return { prevNotifications };
    },
    onError: (_err, _vars, context) => {
      if (context?.prevNotifications && merchant?.id) {
        queryClient.setQueryData(
          notificationsQueryKey(merchant.id),
          context.prevNotifications
        );
      }
    },
    onSuccess: () => {
      // Server response confirms the update, cache is already optimistically updated
    },
  });
}

/**
 * Helper function to add a notification to cache
 * Used by WebSocket handlers when a notification arrives. A grouped entry
 * (one per conversation) arrives again with the same id when a later message
 * updates it: it replaces its copy and moves to the top.
 */
export function appendNotificationToCache(
  queryClient: ReturnType<typeof useQueryClient>,
  merchantId: number,
  notification: NotificationDomain
) {
  queryClient.setQueryData<NotificationDomain[]>(
    notificationsQueryKey(merchantId),
    (old) => {
      if (!old) return [notification];
      return [notification, ...removeById(old, notification.id)];
    }
  );
}
